/**
 * Shared search bar with optional filter button and filter chips.
 * Consolidates the repeated search + filter UI pattern
 *
 * Owns its own form state for the search input. When onFilterClick is
 * omitted, the Filters button is not rendered.
 */
import { Button, Input } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { IconV3 } from "@rpi/openapi-core";
import { SearchInput } from "../../configuration/filters/searchInput";
import { FilterChips } from "../FilterChips";
import { useSearchStyles } from "./searchStyles";
import { PropsWithChildren } from "react";

type SearchBarProps = {
  placeholder: string;
  isLoading: boolean;
  onSearchSubmit: (searchValue: string) => void;
  // Renders a multi-line overlay placeholder so long text wraps instead of truncating.
  wrapPlaceholder?: boolean;
};

type FilterBarProps<TField extends string> = {
  filters: SearchInput<TField>[];
  onSetFilters: (recipe: (prev: SearchInput<TField>[]) => SearchInput<TField>[]) => void;
  onFilterClick: () => void;
  filterLabels?: Partial<Record<TField, string>>;
  valueRenderers?: Partial<Record<TField, (value: string) => string>>;
};

export function SearchBar({
  placeholder,
  isLoading,
  onSearchSubmit,
  wrapPlaceholder,
  children,
}: PropsWithChildren<SearchBarProps>) {
  const { classes } = useSearchStyles();
  const form = useForm<{ searchValue: string }>({ initialValues: { searchValue: "" } });

  const handleSubmit = (input: { searchValue: string }) => {
    onSearchSubmit(input.searchValue || "");
  };

  const showWrappingPlaceholder = wrapPlaceholder && !form.values.searchValue;

  return (
    <form className={classes.search} onSubmit={form.onSubmit(handleSubmit)}>
      <div className={wrapPlaceholder ? classes.searchInputWrap : classes.searchInput}>
        <Input
          iconLeft={<IconV3 icon="search" size={24} />}
          iconRight={
            form.values.searchValue && (
              <Button
                type="reset"
                aria-label="Clear Search"
                className={classes.searchClearButton}
                variant="text"
                intent="neutral"
                onClick={() => form.setFieldValue("searchValue", "")}
              >
                <IconV3 icon="close" size={24} />
              </Button>
            )
          }
          placeholder={placeholder}
          disabled={isLoading}
          {...form.getInputProps("searchValue")}
        />
        {showWrappingPlaceholder && (
          <span className={classes.wrappingPlaceholder} aria-hidden="true">
            {placeholder}
          </span>
        )}
      </div>
      <Button variant="outlined" intent="neutral" type="submit" disabled={isLoading}>
        Search
      </Button>
      {children}
    </form>
  );
}

export function SearchFilterBar<TField extends string>({
  placeholder,
  isLoading,
  filters = [],
  onSetFilters,
  onSearchSubmit,
  onFilterClick,
  filterLabels,
  valueRenderers,
  wrapPlaceholder,
}: SearchBarProps & FilterBarProps<TField>) {
  const { classes } = useSearchStyles();

  return (
    <div className={classes.filtersContainer}>
      <SearchBar
        placeholder={placeholder}
        isLoading={isLoading}
        onSearchSubmit={onSearchSubmit}
        wrapPlaceholder={wrapPlaceholder}
      >
        <Button variant="outlined" intent="neutral" onClick={onFilterClick} disabled={isLoading}>
          <IconV3 icon="tune" color="inherit" size={20} />
          Filters
        </Button>
      </SearchBar>
      {filters.length > 0 && (
        <FilterChips
          filters={filters}
          onSetFilters={onSetFilters}
          filterLabels={filterLabels}
          valueRenderers={valueRenderers}
        />
      )}
    </div>
  );
}
